import { apiClient } from './api';
import type {
  GetEmployeeResponse,
  GetAllEmployeesApiRequest,
  GetManagerEmployeesApiRequest,
  GetManagerEmployeesApiResponse,
  GetEmployeeRelationsResponse,
  EmployeeInfo,
  OnboardingApiRequest,
} from '@momentum/types';

export const employeesService = {
  async onboarding(data?: Partial<OnboardingApiRequest>) {
    return apiClient.post('/employees/onboarding', data);
  },

  // Get all employees by company ID
  async getAllEmployees(
    params: GetAllEmployeesApiRequest
  ): Promise<GetEmployeeResponse[]> {
    const queryParams = new URLSearchParams();

    if (params.isDeleted !== undefined) {
      queryParams.append('isDeleted', params.isDeleted.toString());
    }
    if (params.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }

    return (await apiClient.get(`/employees?${queryParams.toString()}`)).data;
  },

  // Get a single employee by ID
  async getEmployeeById(
    id: string,
    companyId: string
  ): Promise<GetEmployeeResponse> {
    return (await apiClient.get(`/employees/${id}?companyId=${companyId}`))
      .data;
  },

  // Get employee manager
  async getEmployeeManager(
    id: string,
    companyId: string
  ): Promise<EmployeeInfo | null> {
    return (
      await apiClient.get(`/employees/${id}/manager?companyId=${companyId}`)
    ).data;
  },

  // Get employee relations (manager, peers, employees)
  async getEmployeeRelations(
    id: string,
    companyId: string
  ): Promise<GetEmployeeRelationsResponse> {
    return (
      await apiClient.get(`/employees/${id}/relations?companyId=${companyId}`)
    ).data;
  },

  async getManagerEmployees(
    params: GetManagerEmployeesApiRequest
  ): Promise<GetManagerEmployeesApiResponse> {
    const queryParams = new URLSearchParams();

    queryParams.append('managerId', params.managerId);

    if (params.excludeManager !== undefined) {
      queryParams.append('excludeManager', params.excludeManager.toString());
    }

    return (
      await apiClient.get(
        `/employees/manager/employees?${queryParams.toString()}`
      )
    ).data;
  },

  async getManagerDirectEmployees(
    params: GetManagerEmployeesApiRequest
  ): Promise<GetManagerEmployeesApiResponse> {
    const queryParams = new URLSearchParams();

    queryParams.append('managerId', params.managerId);

    return (
      await apiClient.get(
        `/employees/manager/direct-employees?${queryParams.toString()}`
      )
    ).data;
  },

  // Get eligible coworkers (excludes manager and direct reports)
  async getEligibleCoworkers(): Promise<GetEmployeeResponse[]> {
    return (await apiClient.get('/employees/eligible-coworkers')).data;
  },
};
